/**
 * 🔴 **THE TWO STEPS AN MCP CLIENT'S SIGN-IN TAKES THROUGH THE DESK** (D133).
 *
 * The authorization server sends the browser to `/oauth/authorize?<signed query>` when a person has
 * to sign in, and to `/oauth/consent?<signed query>` when they have to allow the client. The query is
 * signed by the server (`sig`, `exp`) and is handed back to it untouched — this file never reads a
 * meaning into it beyond carrying it. Signing in with Google leaves the page, so the query is kept in
 * `sessionStorage` for the return trip.
 */
import { API_BASE_URL } from "@/api/base";

const STASH = "pinksy.oauth.query";
export const AUTHORIZE_PATH = "/identity/oauth2/authorize";
const CONSENT_PATH = "/identity/oauth2/consent";
const PUBLIC_CLIENT_PATH = "/identity/oauth2/public-client";

/** The signed query this page arrived with, or the one stashed before the browser left for Google. */
export function oauthQuery(search: string = window.location.search): string {
  const present = search.replace(/^\?/u, "");
  if (present !== "" && new URLSearchParams(present).has("client_id")) {
    try {
      sessionStorage.setItem(STASH, present);
    } catch {
      // No storage: the round trip through Google will lose the query and the client retries.
    }
    return present;
  }
  try {
    return sessionStorage.getItem(STASH) ?? "";
  } catch {
    return "";
  }
}

export function clearOAuthQuery(): void {
  try {
    sessionStorage.removeItem(STASH);
  } catch {
    // Nothing to clear.
  }
}

/** Where a signed-in person is sent to resume the authorization: the server's own authorize endpoint, with the signed query, through the same origin. */
export const resumeUrl = (query: string): string => `${API_BASE_URL}${AUTHORIZE_PATH}?${query}`;

export interface OAuthClientView {
  clientId: string;
  name: string;
  uri: string | null;
  scopes: string[];
}

/** What the consent page shows: the client's registered name, from the server. Never trusted from the query. */
export async function describeClient(query: string, fetchImpl: typeof fetch = fetch): Promise<OAuthClientView | null> {
  const params = new URLSearchParams(query);
  const clientId = params.get("client_id") ?? "";
  if (clientId === "") return null;
  try {
    const response = await fetchImpl(`${API_BASE_URL}${PUBLIC_CLIENT_PATH}?client_id=${encodeURIComponent(clientId)}`, { credentials: "include" });
    if (!response.ok) return null;
    const body = (await response.json()) as { client_id?: string; client_name?: string; client_uri?: string | null };
    return { clientId, name: body.client_name ?? clientId, uri: body.client_uri ?? null, scopes: (params.get("scope") ?? "").split(" ").filter((scope) => scope !== "") };
  } catch {
    return null;
  }
}

/** The person's answer, to the server; it answers with where to send the browser (back to the client, with a code, or with an error). */
export async function answerConsent(query: string, accept: boolean, fetchImpl: typeof fetch = fetch): Promise<string | null> {
  try {
    const response = await fetchImpl(`${API_BASE_URL}${CONSENT_PATH}`, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ accept, oauth_query: query }),
    });
    if (!response.ok) return null;
    // The provider answers a browser fetch with `{ redirect: true, url }` (its `handleRedirect`); `redirect_uri` is the documented name kept as a fallback.
    const body = (await response.json()) as { url?: unknown; redirect_uri?: unknown };
    if (typeof body.url === "string" && body.url !== "") return body.url;
    return typeof body.redirect_uri === "string" ? body.redirect_uri : null;
  } catch {
    return null;
  }
}
